import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";

import { Heading } from "components/Heading";
import { LoaderFallbackUI } from "components/LoaderFallbackUI";
import DataTable from "components/DataTable/DataTable";
import { getUserTests } from "api/api.service";
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils";
import typography from "../typography";

const GRAPH_COLORS = { anxiety: "#7b61ff", depression: "#06402b", gridBorder: "#f6f6f6" };
const DAYS_WINDOW = 180;

// GAD-7 (ANXIETY, 0-21) and PHQ-9 (DEPRESSION, 0-27) severity bands, matching the
// clinical scoring the mobile app and product docs already use.
const SEVERITY_BANDS = {
    ANXIETY: [
        { max: 4, label: "Minimal" },
        { max: 9, label: "Mild" },
        { max: 14, label: "Moderate" },
        { max: Infinity, label: "Severe" },
    ],
    DEPRESSION: [
        { max: 4, label: "Minimal" },
        { max: 9, label: "Mild" },
        { max: 14, label: "Moderate" },
        { max: 19, label: "Moderately Severe" },
        { max: Infinity, label: "Severe" },
    ],
};

const computeScore = (userTest) =>
    (userTest.answers || []).reduce((sum, a) => sum + (a.answerOption?.score ?? 0), 0);

const severityFor = (type, score) =>
    (SEVERITY_BANDS[type] || []).find((band) => score <= band.max)?.label || "-";

const CHART_OPTIONS = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { curve: "smooth", width: 2 },
    markers: { size: 4 },
    dataLabels: { enabled: false },
    xaxis: {
        type: "category",
        labels: { style: { fontSize: "12px", fontFamily: "Poppins" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
    },
    grid: { borderColor: GRAPH_COLORS.gridBorder },
};

export default function WellnessTests() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [tests, setTests] = useState([]);

    useEffect(() => {
        setLoading(true);
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - DAYS_WINDOW);

        getUserTests({
            userId: id,
            start_date: convertDateTime({ date: start, customFormat: dateTimeFormat.apiDateTimeFormat }),
            end_date: convertDateTime({ date: end, customFormat: dateTimeFormat.apiDateTimeFormat }),
            limit: 200,
            column: "createdAt",
            direction: "ASC",
        })
            .then((res) => setTests(res?.data || []))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <LoaderFallbackUI isLoader={loading} />;

    const byType = (type) => tests.filter((t) => t.test?.type === type);
    const anxiety = byType("ANXIETY");
    const depression = byType("DEPRESSION");

    // Each series carries its own {x, y} pairs rather than sharing one categories array —
    // the two tests are taken independently, so their dates rarely line up 1:1, and a
    // shared array would silently mis-align scores against the wrong date for whichever
    // series has fewer points.
    const toSeriesData = (attempts) =>
        attempts.map((t) => ({
            x: convertDateTime({ date: t.createdAt, customFormat: "MMM DD" }) || "",
            y: computeScore(t),
        }));

    const series = [
        anxiety.length && { name: "GAD-7 (Anxiety)", data: toSeriesData(anxiety) },
        depression.length && { name: "PHQ-9 (Depression)", data: toSeriesData(depression) },
    ].filter(Boolean);

    return (
        <div className="profile-details-con p-4 mt-3">
            <Heading heading={typography.WELLNESS_TESTS} />
            {tests.length === 0 ? (
                <p className="data-not-found-typo standard-typo-16-600 mt-3">
                    {typography.NO_WELLNESS_TESTS}
                </p>
            ) : (
                <>
                    <Card className="mt-3">
                        <CardBody>
                            <ReactApexChart
                                type="line"
                                series={series}
                                options={{
                                    ...CHART_OPTIONS,
                                    colors: [GRAPH_COLORS.anxiety, GRAPH_COLORS.depression],
                                }}
                                height={280}
                            />
                        </CardBody>
                    </Card>
                    <DataTable
                        data={[...tests].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                        loading={false}
                        config={[
                            {
                                title: typography.TEST_NAME,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {row.test?.name || "-"}
                                    </div>
                                ),
                            },
                            {
                                title: typography.SCORE,
                                render: (row) => (
                                    <div className="standard-typo-14-400">{computeScore(row)}</div>
                                ),
                            },
                            {
                                title: typography.SEVERITY,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {severityFor(row.test?.type, computeScore(row))}
                                    </div>
                                ),
                            },
                            {
                                title: typography.DATE,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {convertDateTime({
                                            date: row.createdAt,
                                            customFormat: dateTimeFormat.monthLetterDateFormat,
                                        }) || "-"}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </>
            )}
        </div>
    );
}
